import React, {
  useEffect,
  useState,
  useMemo,
  memo,
  useLayoutEffect,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../../api';
import { useInsurance } from '../../../context/Insurance';
import shade from '../../../utils/shade';
import { UNIFIED_ACTION, IQuotePartnerData } from '../../../utils/types';
import CardLoader from '../../CardLoader';
import PolicyPurchaseConfirmation from '../../common/PolicyPurchaseConfirmation';
import Tooltip from '../../common/Tooltip';
import Modal from '../../Modal/index';
import { RotateLoader } from 'react-spinners';
import PolicyForm from './Screens/PolicyForm';
import useConfigStore from '../../../stores/configStore';
import useWidgetStore, { CurrentTabEnum } from '../../../stores/widgetStore';
import useCustomerStore from '../../../stores/customerStore';
import useSaasPlatformStore from '../../../stores/saasPlatformStore';
import useProductStore from '../../../stores/productStore';
import useLatestQuotesWithPolicies from '../../../hooks/useLatestQuotesWithPolicies';
import useWebsocket from '../../../hooks/useWebsocket';
import useLatestPolicy from '../../../hooks/useLatestPolicy';
import useErrorStore from '../../../stores/errorStore';
import useErrorHandledMutation from '../../../hooks/useErrorHandledMutation';
import ErrorScreen from './ErrorScreen';
import QuoteLoading from './QuoteLoading';
import LimitFlow from './LimitFlow.tsx';
import PolicyListing from './PolicyListing';
import { parseISO, addYears, format } from 'date-fns';
import PolicyStepsHeader from '../../common/PolicyStepsHeader';
import VerifiedIcon from '../../icons/VerifiedIcon';
import QuotePending from './Screens/QuotePending';
import QuoteRejected from './Screens/QuoteRejected';
import PolicyPending from './Screens/PolicyPending';
import SelectApplicationDetails from './Screens/SelectApplicationDetails';
import Payment from '../Payment/Payment.tsx';
import LimitPolicySelection from './LimitPolicySelection.tsx';
import BrizaPaymentForm from './Screens/BrizaPaymentForm.tsx';
import NoPaymentPurchase from './Screens/NoPaymentPurchase.tsx';
import PolicyAuditStartRequest from './Screens/PolicyAuditStartRequest';
import MultiQuoteScreen from '../../MultiQuote/MultiQuoteScreen.tsx';

const getQuoteInfo = (quoteDetails) => {
  switch (quoteDetails?.partnerIdentifier) {
    case 'tigerLabs':
      return {
        quoteId: quoteDetails?.partnerData?.rateQuote?.number,
        insuranceProvider: 'TigerLabs',
        quoteExpense: (
          quoteDetails?.total -
          Number(quoteDetails?.partnerData?.rateQuote?.iptAmount)
        )?.toFixed(2),
        quoteIPT: quoteDetails?.partnerData?.rateQuote?.iptAmount,
        quoteTotalAmount: quoteDetails?.total?.toFixed(2),
      };
    case 'opal':
      return {
        quoteId: `${quoteDetails?.partnerData?.identifier?.split('-')[0]}-${
          quoteDetails?.partnerData?.identifier?.split('-')[0]
        }`,
        insuranceProvider: 'Opal',
        quoteExpense: (
          quoteDetails?.partnerData?.premium?.gross -
          0.2 * quoteDetails?.partnerData?.premium?.gross
        )?.toFixed(2),
        quoteIPT: (
          (quoteDetails?.partnerData?.premium?.gross / 100) *
          20
        )?.toFixed(2),
        quoteTotalAmount: quoteDetails?.partnerData?.premium?.gross?.toFixed(2),
      };
    case 'hiscox':
      return {
        quoteId:
          quoteDetails?.partnerData?.quote?.quoteResult?.policy
            ?.externalQuoteId,
        insuranceProvider: 'hiscox',
        quoteExpense: (
          Number(quoteDetails?.total) -
          0.2 * Number(quoteDetails?.total)
        )?.toFixed(2),
        quoteIPT: ((Number(quoteDetails?.total) / 100) * 20)?.toFixed(2),
        quoteTotalAmount: quoteDetails?.total?.toFixed(2),
        paymentOptions:
          quoteDetails?.partnerData?.quote?.quoteResult?.policy?.paymentOptions,
        collectPayment: true,
      };
    case 'brizaUnifiedHiscox':
      return {
        quoteId:
          quoteDetails?.partnerData?.quotes?.[
            Object.keys(quoteDetails?.partnerData?.quotes)[0]
          ]?.id,
        insuranceProvider: 'brizaUnifiedHiscox',
        quoteExpense: (
          Number(quoteDetails?.total) -
          0.2 * Number(quoteDetails?.total)
        )?.toFixed(2),
        quoteIPT: ((Number(quoteDetails?.total) / 100) * 20)?.toFixed(2),
        quoteTotalAmount: quoteDetails?.total?.toFixed(2),
        paymentOptions:
          quoteDetails?.partnerData?.quotes[
            Object.keys(quoteDetails?.partnerData?.quotes)[0]
          ]?.paymentOptions,
        collectPayment:
          quoteDetails?.partnerData?.quotes[
            Object.keys(quoteDetails?.partnerData?.quotes)[0]
          ]?.collectPayment,
      };
    default:
      return {
        quoteId: `${quoteDetails?.id?.split('-')[0]}-${
          quoteDetails?.id?.split('-')[1]
        }`,
        insuranceProvider: quoteDetails?.partnerData?.identifier || 'WTW',
        quoteExpense: (
          Number(quoteDetails?.total) -
          0.2 * Number(quoteDetails?.total)
        )?.toFixed(2),
        quoteIPT: ((Number(quoteDetails?.total) / 100) * 20)?.toFixed(2),
        quoteTotalAmount: quoteDetails?.total?.toFixed(2),
      };
  }
};

const Policy = memo(({ scrollableElementRef }) => {
  const {
    selectedQuote,
    setSelectedQuote,
    isFormActive,
    modalOpen,
    currQuestionGroup,
    isEditRequested,
    setModalOpen,
    setCurrentTab,
    setSelectedPolicy,
    setCurrQuestionGroup,
    setActiveDraftQuote,
    setIsEditRequested,
    applicationDataId,
    setFormActive,
    createQuoteSubmitted,
    setAnswers,
    setApplicationDataId,
    selectedPolicyPayload,
    policyPaymentPayloads,
    setSelectedPolicyPayload,
    unifiedState,
    setUnifiedState,
  } = useWidgetStore();
  const [isBrizaPayment, setIsBrizaPayment] = useState(null);
  const [brizaPaymentSuccess, setBrizaPaymentSuccess] = useState(false);
  const [purchaseConfirmation, setPurchaseConfirmation] = useState(false);
  const [restartRequest, setRestartRequest] = useState(false);
  const [latestQuoteStatus, setLatestQuoteStatus] = useState();
  const [showList, setShowList] = useState(false);
  const { keys, style } = useConfigStore();
  const { primaryColor, logo, theme } = style;
  const { widgetType, token } = keys;
  const {
    extCustomerName: customerName,
    extCustomerID: externalID,
    setExternalCustomer,
    applicationFormData,
    policyAuditEngine,
    ...extCustomer
  } = useCustomerStore();
  const { brokerFormLink } = useSaasPlatformStore();
  const { setProduct } = useProductStore();
  const { error, setError } = useErrorStore();
  const { errorHandledMutation } = useErrorHandledMutation();
  const latestQuotesWithPolicies = useLatestQuotesWithPolicies(true);
  const latestPolicyWithoutFilter = useLatestPolicy();
  const [bindingQuoteKeys, setBindingQuoteKeys] = useState([]);
  const requestedProductId = useMemo(() => {
    if (!applicationFormData) {
      return null;
    }

    const currentApplication = applicationDataId
      ? applicationFormData[applicationDataId]
      : Object.values(applicationFormData)[0];

    return currentApplication?.productId || null;
  }, [applicationFormData, applicationDataId]);

  const relevantQuotes = useMemo(() => {
    return latestQuotesWithPolicies.filter((quote) => {
      const ignorePolicy =
        quote?.product?.Underwriter?.widgetFlow === 'racetrac' &&
        Boolean(applicationFormData?.[quote?.externalId || '']) === false;

      if (ignorePolicy) {
        return false;
      }

      if (quote?.product?.Underwriter?.widgetFlow !== 'hartford_paf') {
        return true;
      }

      if (!requestedProductId) {
        return true;
      }

      return (
        String(quote?.ProductId || quote?.product?.id || '') ===
        String(requestedProductId)
      );
    });
  }, [latestQuotesWithPolicies, requestedProductId]);

  const { latestQuote, latestPolicy } = useMemo(() => {
    const latestQuote =
      relevantQuotes.find((q) => q.id === selectedQuote) || null;
    const latestPolicy = latestQuote?.policy || null;
    return { latestQuote, latestPolicy };
  }, [relevantQuotes, selectedQuote]);

  const queryClient = useQueryClient();

  const customer = queryClient.getQueryState(['customer'])?.data;

  const { data: raceTracDocuments, isLoading } = useQuery(
    ['documents', latestQuote?.id],
    {
      queryFn: async () => {
        const response = await api.quote.getSignatureDocuments({
          quoteId: latestQuote?.id,
        });
        return response.data;
      },
      enabled: Boolean(latestQuote && latestQuote?.id && !isEditRequested),
    },
  );

  const { policyDocsExist, allDocsSigned } = useMemo(() => {
    const urls = raceTracDocuments?.urls || [];
    const minSignedDocs = raceTracDocuments?.minSignedDocs;

    const hasPolicyDocuments = urls.length > 0;

    const signedDocs = urls.filter((doc) => doc.key.endsWith('(signed).pdf'));
    const signedCount = signedDocs.length;

    return {
      policyDocsExist:
        hasPolicyDocuments &&
        (urls.some((doc) => !doc.key.endsWith('(signed).pdf')) ||
          signedCount < minSignedDocs),
      allDocsSigned:
        hasPolicyDocuments &&
        signedCount >= minSignedDocs &&
        urls.every((doc) => doc.key.endsWith('(signed).pdf')),
    };
  }, [raceTracDocuments]);

  useEffect(() => {
    if (brizaPaymentSuccess) {
      // Check if there are more plans to process
      if (
        selectedPolicyPayload?.selectedPlan &&
        selectedPolicyPayload.selectedPlan.length > 1
      ) {
        // Remove the first plan (just processed) and set the next one
        const remainingPlans = selectedPolicyPayload.selectedPlan.slice(1);

        setSelectedPolicyPayload({
          ...selectedPolicyPayload,
          selectedPlan: remainingPlans,
        });

        // Set the next plan for payment
        // FIX: Ensure we are setting the 'isBrizaPayment' state to the exact same structure expected by the form
        const nextPlanData = remainingPlans[0];

        setIsBrizaPayment({
          ...nextPlanData.selectedPlan, // Spread the payment terms (downpayment, etc)
          quoteKey: nextPlanData.quoteKey,
          label: nextPlanData.label || 'Policy', // Optional: Add label if available in your payload
          externalQuoteReference: nextPlanData.externalQuoteReference,
        });

        setBrizaPaymentSuccess(false);
      } else if (latestPolicy) {
        // All plans processed, reset state
        setBrizaPaymentSuccess(false);
        setIsBrizaPayment(null);
        setSelectedPolicyPayload(null);
      }
    }
  }, [brizaPaymentSuccess, latestPolicy, selectedPolicyPayload, latestQuote]);

  // Check if selectedPolicyPayload exists and set isBrizaPayment
  useEffect(() => {
    if (
      selectedPolicyPayload?.selectedPlan &&
      selectedPolicyPayload?.selectedPlan?.length > 0
    ) {
      const firstPlan = selectedPolicyPayload.selectedPlan[0];

      setIsBrizaPayment({
        ...firstPlan.selectedPlan,
        quoteKey: firstPlan.quoteKey,
        externalQuoteReference: firstPlan.externalQuoteReference,
        quoteId: firstPlan.quoteId,
      });
    }
  }, [selectedPolicyPayload]);

  const createKaynaHash = useMutation(api.auth.generateTempToken, {
    onSuccess: (resp) => {
      if (brokerFormLink) {
        const url = new URL(brokerFormLink);
        url.searchParams.set('kaynaToken', resp?.data?.data?.token);
        url.searchParams.set('extCustomerID', externalID);
        window.open(url.toString());
        setModalOpen(false);
        setCurrentTab(CurrentTabEnum.POLICY);
      }
    },
  });

  const getBrizaStepInfo = useMemo(() => {
    if (!selectedPolicyPayload?.selectedPlan) return { current: 1, total: 1 };

    // policyPaymentPayloads holds the completed ones
    const completedCount = policyPaymentPayloads?.length || 0;
    // selectedPolicyPayload.selectedPlan holds the remaining ones
    const remainingCount = selectedPolicyPayload.selectedPlan.length;

    const total = completedCount + remainingCount;
    const current = completedCount + 1;

    return { current, total };
  }, [policyPaymentPayloads, selectedPolicyPayload]);

  const handleCreateKaynaHash = (applicationFormId) => {
    errorHandledMutation(() =>
      createKaynaHash.mutateAsync({
        token: token,
        applicationFormId: applicationFormId,
      }),
    );
  };

  const createQuote = useMutation(api.quote.create, {
    retry: 3,
    onSuccess: (resp) => {
      if (widgetType == 'broker') {
        handleCreateKaynaHash(resp.data.quote.externalId);
      } else {
        queryClient.invalidateQueries(['customer']);
      }
    },
  });

  const handleCreatePolicy = useMutation(api.policy.create, {
    retry: 3,
    onSuccess: (response) => {
      queryClient.invalidateQueries(['customer']);
      if (response?.data?.data?.insuranceStatus == 'active') {
        setSelectedPolicy({
          policyId: response?.data?.data?.id,
          externalId: response?.data?.data?.externalId,
        });
        setCurrentTab(CurrentTabEnum.POLICY_INFO);
      } else {
        setUnifiedState({});
        if (latestQuote.partnerIdentifier !== 'brizaUnifiedHiscox') {
          setSelectedPolicy({
            policyId: response?.data?.data?.id,
            externalId: response?.data?.data?.externalId,
          });
          setSelectedQuote(
            response?.data?.data?.policy?.QuoteId ||
              response?.data?.data?.QuoteId,
          );
        }
        setIsBrizaPayment(null);
      }
    },
  });

  const createQuoteMutation = (items, productId, quoteExternalRef) => {
    return errorHandledMutation(() =>
      createQuote.mutateAsync({
        customerID: customer?.data?.data?.id,
        productId: productId,
        items: items ? items : extCustomer.items,
        saasId: keys.platformId,
        externalID: customer?.data?.data?.externalId,
        quoteExternalId: quoteExternalRef,
      }),
    );
  };

  const purchaseQuoteMutation = (paymentPayload = null) => {
    setBindingQuoteKeys((prev) => [...prev, isBrizaPayment?.quoteKey]);
    return errorHandledMutation(() =>
      handleCreatePolicy.mutateAsync({
        quoteId: unifiedState.activeQuoteId || latestQuote?.id,
        customerID: customer.data.data.id,
        customerName,
        externalID,
        insuranceStatus: 'active',
        paymentStatus: 'active',
        cycle: 'yearly',
        widgetFlow: latestQuote?.partnerIdentifier || null,
        paymentPayload: paymentPayload,
      }),
    );
  };

  const updateTab = (e, number) => {
    e.preventDefault();
    setCurrentTab(number);
  };

  useLayoutEffect(() => {
    if (latestQuote) {
      if (
        latestQuote.status == 'pending' ||
        latestQuote.status == 'draft' ||
        (unifiedState?.action &&
          unifiedState?.activeQuoteId &&
          unifiedState?.externalId)
      ) {
        if (
          latestQuote.partnerIdentifier === 'brizaUnifiedHiscox' &&
          latestQuote.partnerData
        ) {
          const activeQuote = latestQuotesWithPolicies.find(
            (q) => q.id === unifiedState.activeQuoteId,
          );
          const partnerData = latestQuote.partnerData;
          const isError =
            partnerData.error !== null && partnerData.error !== undefined;
          const showCoverageOrAck =
            unifiedState?.action ===
              UNIFIED_ACTION.SHOW_COVERAGE_QUESTIONS_TO_FE ||
            unifiedState?.action === UNIFIED_ACTION.SHOW_ACK_QUESTIONS_TO_FE;
          const isQuestionsToShow =
            partnerData.action === UNIFIED_ACTION.SHOW_QUESTIONS_TO_FE &&
            Object.keys(partnerData.questions || {}).length;

          if (isQuestionsToShow || showCoverageOrAck || isError) {
            const answers = {
              ...applicationFormData?.[latestQuote.externalId]
                ?.applicationDetails,
              ...(activeQuote || latestQuote).items.requiredParams,
            };
            setAnswers(answers);
            setProduct(latestQuote.product);
            setApplicationDataId(latestQuote.externalId);
            setFormActive(true);
            setShowList(true);
            if (showCoverageOrAck || isError) {
              setLatestQuoteStatus('draft');
            } else {
              setLatestQuoteStatus(latestQuote.status);
            }
          } else {
            setLatestQuoteStatus(latestQuote.status);
          }
        } else {
          setLatestQuoteStatus(latestQuote.status);
        }
        // if (latestQuote.status === 'pending') {
        //   handleQuoteWebsockes(latestQuote.id, () =>
        //     queryClient.invalidateQueries(['customer']),
        //   );
        // }
      } else {
        let isAnyPending = false;
        if (latestQuote.partnerIdentifier === 'brizaUnifiedHiscox') {
          isAnyPending = latestQuotesWithPolicies?.some((q) => {
            return (
              q.partnerData?.action === UNIFIED_ACTION.CREATE_PRE_APPLICATION &&
              q.status === 'pending'
            );
          });
        }
        if (isAnyPending) {
          setLatestQuoteStatus('pending');
        } else {
          setLatestQuoteStatus(latestQuote.status);
        }
      }
    } else {
      setLatestQuoteStatus(undefined);
    }
  }, [latestQuote, unifiedState]);

  useEffect(() => {
    if (selectedQuote && !modalOpen) {
      const isPart = relevantQuotes?.some((q) => q.id === selectedQuote);

      if (!isPart || Object.keys(applicationFormData).length > 1) {
        setSelectedQuote(null);
      }
    }
  }, [relevantQuotes, selectedQuote, modalOpen]);

  useEffect(() => {
    const policyKeys = Object.keys(applicationFormData);
    if (
      policyKeys.length === 1 &&
      relevantQuotes.length === 1 &&
      relevantQuotes[0].status !== 'draft'
    ) {
      setSelectedQuote(relevantQuotes[0].id);
    }
  }, [relevantQuotes, modalOpen]);

  // useEffect(() => {
  //   if (latestPolicy && latestPolicy.insuranceStatus == 'pending') {
  //     handlePolicyWebsockes(latestPolicy.id, () => {
  //       queryClient.invalidateQueries(['customer']).then(() => {
  //         if (modalOpen) {
  //           setCurrentTab(CurrentTabEnum.POLICY_INFO);
  //         }
  //       });
  //     });
  //   }
  // }, [latestPolicy]);

  useEffect(() => {
    if (!modalOpen) {
      setFormActive(false);
      setIsBrizaPayment(null);
    }
  }, [modalOpen]);

  useEffect(() => {
    if (widgetType === 'broker') {
      setSelectedQuote(null);
    }
  }, [selectedQuote, latestQuotesWithPolicies]);

  const onQuoteMessageHandler = () => {
    queryClient.invalidateQueries(['customer']);
    queryClient.invalidateQueries(['documents']);
  };
  const onPolicyMessageHandler = () => {
    queryClient.invalidateQueries(['customer']).then(() => {
      if (modalOpen) {
        setCurrentTab(CurrentTabEnum.POLICY_INFO);
      }
    });
  };

  useWebsocket({
    onMessage: onQuoteMessageHandler,
    enabled:
      latestQuote &&
      (latestQuote.status === 'pending' ||
        latestQuote.partnerIdentifier === 'brizaUnifiedHiscox'),
    url: `${process.env.REACT_APP_QUOTE_WEBSOCKET_URL}?quoteId=${latestQuote?.id}`,
  });

  useWebsocket({
    onMessage: onQuoteMessageHandler,
    enabled:
      latestQuote && latestQuote.partnerIdentifier === 'brizaUnifiedHiscox',
    url: `${process.env.REACT_APP_POLICY_WEBSOCKET_URL}?policyId=${latestQuote?.id}`,
  });

  useWebsocket({
    onMessage: onPolicyMessageHandler,
    enabled:
      latestPolicy &&
      latestPolicy.insuranceStatus == 'pending' &&
      latestQuote?.partnerIdentifier !== 'brizaUnifiedHiscox',
    url: `${process.env.REACT_APP_POLICY_WEBSOCKET_URL}?policyId=${latestPolicy?.id}`,
  });

  const startPurchaseHandler = () => {
    setModalOpen(true);
    setPurchaseConfirmation(true);
  };

  const selectPolicyHandler = (quote) => {
    setSelectedQuote(quote.id);
    startPurchaseHandler();

    const answers = {
      ...applicationFormData?.[quote.externalId]?.applicationDetails,
      ...quote.items.requiredParams,
    };

    if (
      quote.status == 'draft' &&
      quote.items?.otherParams?.currQuestionGroup !== null &&
      quote.items?.otherParams?.currQuestionGroup !== undefined
    ) {
      setActiveDraftQuote(quote.id);
      setCurrQuestionGroup(quote.items?.otherParams?.currQuestionGroup);
    }
    setProduct(quote.product);
    setAnswers(answers);
    setApplicationDataId(quote.externalId);
    setFormActive(true);

    if (quote.status === 'bound' || quote.status === 'MT Draft') {
      setSelectedPolicy({
        policyId: quote.policy.id,
        externalId: quote?.externalId,
      });
      setCurrentTab(CurrentTabEnum.POLICY_INFO);
    }
  };

  const restartHandler = (val) => {
    setRestartRequest(val);

    if (latestQuote) {
      const answers = {
        ...applicationFormData?.[latestQuote.externalId]?.applicationDetails,
        ...latestQuote.items.requiredParams,
      };

      setProduct(latestQuote.product);
      setAnswers(answers);
      setApplicationDataId(latestQuote.externalId);

      if (currQuestionGroup > 0) {
        setCurrQuestionGroup(0);
      }
    }
  };

  const widgetFlow = useMemo(() => {
    const productId = applicationFormData[applicationDataId]?.productId;
    const product = customer?.data?.data?.SaasPlatform?.Products?.find(
      (p) => p.id === productId,
    );

    return (
      product?.Underwriter?.widgetFlow ||
      latestQuote?.product?.Underwriter?.widgetFlow
    );
  }, [applicationFormData, applicationDataId, customer, latestQuote]);

  const isLimit = latestQuote?.product?.Underwriter?.widgetFlow === 'limit';

  if (createQuoteSubmitted) {
    const textHeading = isLimit
      ? 'Preparing your proposal form please wait'
      : widgetFlow === 'hartford_paf'
        ? 'Sending your policies for review.'
        : widgetFlow === 'briza_unified_hiscox'
          ? 'Redirecting you to your customized insurance quotes.'
          : undefined;
    return (
      <QuoteLoading showAnimated textHeading={textHeading} justifyCenter />
    );
  }
  if (error) return <ErrorScreen />;

  if (
    !customer?.data?.data ||
    queryClient.getQueryState(['customer'])?.status === 'loading' ||
    (!brokerFormLink && widgetType == 'broker' && !error)
  ) {
    return <QuoteLoading />;
  }
  if (
    widgetType == 'broker' &&
    (createQuote.isLoading || createKaynaHash.isLoading)
  ) {
    return (
      <QuoteLoading
        textHeading="Re-directing you to WTW to get your insurance quote."
        showAnimated
        justifyCenter
      />
    );
  }

  if (createQuote.isLoading || createKaynaHash.isLoading) {
    return <QuoteLoading showAnimated />;
  }
  if (handleCreatePolicy.isLoading) {
    return <QuoteLoading />;
  }

  if (error) return <ErrorScreen />;

  if (!modalOpen && policyAuditEngine) {
    return <PolicyAuditStartRequest />;
  }

  if (relevantQuotes.length > 0 && (!latestQuote || showList) && !modalOpen) {
    return (
      <PolicyListing
        onSelectPolicy={selectPolicyHandler}
        onNewPolicy={() => {
          startPurchaseHandler();
          setActiveDraftQuote(null);
        }}
        brokerPolicy={createQuoteMutation}
        currentQuoteId={latestQuote?.id || null}
      />
    );
  }

  if (
    latestQuote &&
    !latestPolicy &&
    isLimit &&
    ['pending', 'complete'].includes(latestQuoteStatus) &&
    latestQuote.partnerData?.shareableApplication &&
    modalOpen
  ) {
    return <LimitFlow latestQuote={latestQuote} />;
  }

  return (
    <div
      className={`pb-5 sm:mx-4 h-full`}
      style={{ animation: 'fadeSlideIn 220ms ease forwards', height: '100%' }}
    >
      {modalOpen &&
        latestQuote &&
        latestQuoteStatus == 'complete' &&
        !isBrizaPayment &&
        widgetFlow !== 'marriott' && (
          <div className="sticky top-0 left-0 right-0 w-full bg-white z-[1]">
            <PolicyStepsHeader
              steps={[
                {
                  stepText: 'Your Details',
                  isStepActive: false,
                  isStepCompleted: true,
                },
                ...(widgetFlow === 'limit'
                  ? [
                      {
                        stepText: 'More Questions',
                        isStepActive: false,
                        isStepCompleted: false,
                      },
                    ]
                  : []),
                {
                  stepText: 'Your Quotes',
                  isStepActive: true,
                  isStepCompleted: true,
                },
              ]}
              nextButtonText={
                widgetType == 'broker'
                  ? 'Go to dashboard'
                  : latestQuote && !latestPolicy
                    ? 'Purchase Policy'
                    : null
              }
              handleNext={() =>
                widgetType == 'broker'
                  ? handleCreateKaynaHash(latestQuote.externalId)
                  : latestQuote?.partnerIdentifier == 'hiscox'
                    ? !isBrizaPayment
                      ? setIsBrizaPayment(true)
                      : purchaseQuoteMutation()
                    : purchaseQuoteMutation()
              }
              hideNextButton={
                getQuoteInfo(latestQuote).insuranceProvider == 'hiscox' ||
                latestQuote?.product?.Underwriter?.widgetFlow == 'racetrac' ||
                latestQuote?.product?.Underwriter?.widgetFlow ==
                  'briza_unified_hiscox'
              }
              handleClose={() => {
                setModalOpen(false);
                setSelectedQuote(null);
                setUnifiedState({
                  activeQuote: {
                    state: null,
                  },
                });
              }}
            />
          </div>
        )}

      {latestPolicy ? (
        <>
          {latestPolicy?.insuranceStatus == 'inactive' ? (
            <QuoteRejected
              headingText="Oops! It seems that the policy has been cancelled or expired"
              contentText=" "
              buttonText="Generate New Policy"
            />
          ) : latestPolicy?.insuranceStatus == 'pending' ? (
            <PolicyPending />
          ) : (
            <div className="rounded-3xl mx-2 mt-30 px-2 xl:px-10 bg-backgroundColor my-2 flex flex-col h-full justify-center text-textColor">
              <div className="flex flex-col items-center justify-center">
                <VerifiedIcon />
                <span className="text-xl text-center w-full mt-5 text-[#202851]">
                  Policy Number:
                </span>
                <span className="text-2xl font-bold text-center w-full mt-2 text-[#1F2751]">
                  {latestPolicy.partnerIdentifier === 'limit'
                    ? latestPolicy.partnerData.id
                    : ['hiscox', 'brizaunifiedhiscox'].includes(
                          latestPolicy?.partnerIdentifier,
                        )
                      ? latestPolicy.partnerData?.externalPolicyReference
                      : `${latestPolicy?.id?.split('-')[0]}-${
                          latestPolicy?.id?.split('-')[1]
                        }`}
                </span>

                <p className="text-xs font-bold mt-1 max-w-[288px]">
                  Please be aware that binding and/or policy documents may take
                  up to 5 business days to arrive.
                </p>

                <span className="flex items-center justify-center w-full mt-3.5">
                  <span className="w-2 h-2 rounded-full bg-[#42D58E]" />
                  <span className="ml-2 text-[16px] text-[#202851]">
                    Active until{' '}
                    {latestPolicy?.partnerData == 'tigerLabs'
                      ? format(
                          addYears(
                            parseISO(
                              latestPolicy?.partnerData?.policy?.createdAt,
                            ),
                            1,
                          ),
                          'MM/dd/yyyy',
                        )
                      : latestPolicy?.partnerIdentifier == 'opal'
                        ? format(
                            parseISO(
                              latestPolicy?.partnerData?.policy?.dates?.expiry,
                            ),
                            'MM/dd/yyyy',
                          )
                        : latestPolicy?.createdAt
                          ? format(
                              addYears(new Date(latestPolicy?.createdAt), 1),
                              'MM/dd/yyyy',
                            )
                          : 'Loading...'}
                  </span>
                </span>
              </div>

              <div className="flex flex-col lg:flex-row gap-y-2 gap-x-3 items-center justify-center mt-5">
                {latestPolicy.partnerIdentifier !== 'limit' &&
                  !['hiscox', 'brizaunifiedhiscox'].includes(
                    latestPolicy.partnerIdentifier,
                  ) && (
                    <div className="w-full lg:w-1/2">
                      <button
                        className="w-full h-16 rounded-2xl border-[1px] border-primary m-auto text-primary font-bold tracking-lighter hover:bg-primary hover:text-white px-2"
                        onClick={(e) => {
                          updateTab(e, CurrentTabEnum.COVERAGES);
                          setModalOpen(true);
                        }}
                      >
                        Coverages
                      </button>
                    </div>
                  )}
                <div className="w-full lg:w-1/2">
                  <button
                    className="w-full h-16 rounded-2xl border-[1px] border-primary m-auto text-primary font-bold tracking-lighter hover:bg-primary hover:text-white px-2"
                    onClick={(e) => {
                      updateTab(e, CurrentTabEnum.POLICY_INFO);
                      setModalOpen(true);
                    }}
                  >
                    Policy Details
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {isBrizaPayment ? (
            unifiedState?.collectPayment === false ? (
              <NoPaymentPurchase
                quoteId={
                  isBrizaPayment.externalQuoteReference ||
                  latestQuote?.partnerData?.quote?.quoteResult?.policy
                    ?.externalQuoteId
                }
                selectedPlan={isBrizaPayment}
                setSelectedPlan={setIsBrizaPayment}
                purchaseQuoteMutation={purchaseQuoteMutation}
                onSuccess={() => {
                  setBrizaPaymentSuccess(true);
                }}
              />
            ) : (
              <BrizaPaymentForm
                // FIX: Ensure the key is unique per policy step to force full remount
                key={
                  isBrizaPayment.externalQuoteReference ||
                  isBrizaPayment.quoteKey ||
                  'single-payment'
                }
                quoteId={
                  isBrizaPayment.externalQuoteReference ||
                  latestQuote?.partnerData?.quote?.quoteResult?.policy
                    ?.externalQuoteId
                }
                selectedPlan={isBrizaPayment}
                setSelectedPlan={setIsBrizaPayment}
                purchaseQuoteMutation={purchaseQuoteMutation}
                onSuccess={() => {
                  setBrizaPaymentSuccess(true);
                }}
                isPurchaseLoading={handleCreatePolicy.isLoading}
                stepInfo={getBrizaStepInfo}
              />
            )
          ) : !restartRequest &&
            latestPolicyWithoutFilter &&
            latestPolicyWithoutFilter.insuranceStatus == 'inactive' ? (
            <QuoteRejected
              headingText="Oops! It seems that the policy has been expired/cancelled"
              contentText="You can generate a new policy by clicking restart below"
              setRestartRequest={restartHandler}
            />
          ) : (
            latestQuote !== undefined &&
            !Boolean(latestPolicy) &&
            (latestQuote?.id &&
            latestQuoteStatus == 'rejected' &&
            !restartRequest ? (
              <QuoteRejected
                headingText={
                  Object.values(latestQuote?.partnerData?.quotes || {})[0]
                    ?.failures?.[0]?.reason
                }
                setRestartRequest={restartHandler}
              />
            ) : (latestQuoteStatus == 'pending' ||
                latestQuoteStatus == 'referred' ||
                (latestQuoteStatus == 'complete' && isLimit)) &&
              !restartRequest &&
              !isEditRequested ? (
              <QuotePending
                policyDocsExist={policyDocsExist}
                allDocsSigned={allDocsSigned}
                widgetFlow={widgetFlow}
                action={latestQuote?.partnerData?.action}
              />
            ) : modalOpen &&
              latestQuote?.id &&
              latestQuoteStatus == 'complete' ? (
              widgetFlow === 'marriott' ? (
                <Payment
                  latestQuote={latestQuote}
                  onPurchaseQuote={purchaseQuoteMutation}
                />
              ) : widgetFlow === 'briza_unified_hiscox' ? (
                <MultiQuoteScreen
                  bindingQuoteKeys={bindingQuoteKeys}
                  parentQuoteId={latestQuote.id}
                  parentQuote={JSON.parse(JSON.stringify(latestQuote || {}))}
                  quotes={(() => {
                    const quotes = JSON.parse(
                      JSON.stringify(latestQuote?.partnerData?.quotes || {}),
                    );
                    const keys = Object.keys(quotes);
                    keys.forEach((key) => {
                      if (typeof quotes[key] === 'string') {
                        const childQuote = latestQuotesWithPolicies.find(
                          (q) => q.id === quotes[key],
                        );
                        try {
                          quotes[key] = JSON.parse(JSON.stringify(childQuote));
                        } catch (error) {}
                      } else {
                        quotes[key] = JSON.parse(JSON.stringify(latestQuote));
                      }

                      if (
                        bindingQuoteKeys?.includes(quotes[key]?.externalId) &&
                        quotes[key]?.status === 'binding'
                      ) {
                        setBindingQuoteKeys((prev) =>
                          prev.filter((k) => k !== quotes[key]?.externalId),
                        );
                      }
                    });
                    return quotes;
                  })()}
                />
              ) : (
                <PolicyPurchaseConfirmation
                  quoteId={getQuoteInfo(latestQuote).quoteId}
                  insuranceProvider={
                    getQuoteInfo(latestQuote).insuranceProvider
                  }
                  quoteExpense={getQuoteInfo(latestQuote).quoteExpense}
                  quoteIPT={getQuoteInfo(latestQuote).quoteIPT}
                  quoteTotalAmount={getQuoteInfo(latestQuote).quoteTotalAmount}
                  latestQuote={getQuoteInfo(latestQuote)}
                  setIsBrizaPayment={setIsBrizaPayment}
                />
              )
            ) : brokerFormLink && latestQuote && widgetType == 'broker' ? (
              <div
                className={`rounded-3xl mx-2 mt-30 px-12 bg-backgroundColor my-2 flex flex-col h-full justify-center text-textColor`}
              >
                <div className="flex flex-col items-center justify-center">
                  <VerifiedIcon />
                  <span className="text-2xl font-bold text-center w-full mt-5 text-[#1F2751]">
                    Buy your policy now
                  </span>
                </div>

                <button
                  className={`rounded-2xl bg-primary ${
                    theme == 'night' ? 'rippleNight' : 'ripple'
                  } text-primary font-bold mt-4 mx-auto tracking-lighter px-8 py-4`}
                  style={{
                    backgroundColor:
                      theme == 'night'
                        ? shade(primaryColor, -0.7)
                        : shade(primaryColor, 0.9),
                  }}
                  onClick={() => {
                    setModalOpen(true);
                    setPurchaseConfirmation(true);
                  }}
                >
                  Quote Details
                </button>
              </div>
            ) : (latestQuote?.cerityQuoteId &&
                latestQuote?.quoteQuestions &&
                queryClient.getQueryState(['customer'])?.status !==
                  'loading') ||
              (queryClient.getQueryState(['customer'])?.status !== 'loading' &&
                (!latestQuote ||
                  latestQuoteStatus == 'draft' ||
                  restartRequest ||
                  isEditRequested)) ? (
              isFormActive ? (
                <div
                  key="policy-form"
                  style={{
                    animation: 'fadeSlideIn 220ms ease forwards',
                    height: '100%',
                  }}
                >
                  <PolicyForm
                    scrollableElementRef={scrollableElementRef}
                    setRestartRequest={setRestartRequest}
                    setSelectedQuote={setSelectedQuote}
                    handleCreateKaynaHash={handleCreateKaynaHash}
                    setIsEditRequested={setIsEditRequested}
                  />
                </div>
              ) : (
                <SelectApplicationDetails
                  createQuoteMutation={createQuoteMutation}
                  isLoading={createQuote.isLoading}
                />
              )
            ) : (
              <div
                className={`rounded-3xl mx-2 mt-30 px-12 bg-backgroundColor my-2 flex flex-col h-full justify-center text-textColor`}
              >
                <div className="flex flex-col items-center justify-center">
                  <div className="mb-5">
                    <VerifiedIcon />
                  </div>
                  <span className="text-2xl font-bold text-center w-full text-[#1F2751]">
                    Buy your policy now
                  </span>
                </div>

                <button
                  className={`rounded-2xl bg-primary ${
                    theme == 'night' ? 'rippleNight' : 'ripple'
                  } text-primary font-bold mt-4 mx-auto tracking-lighter px-8 py-4`}
                  style={{
                    backgroundColor:
                      theme == 'night'
                        ? shade(primaryColor, -0.7)
                        : shade(primaryColor, 0.9),
                  }}
                  onClick={startPurchaseHandler}
                >
                  Purchase Policy
                </button>
              </div>
            ))
          )}
        </>
      )}
    </div>
  );
});

export default Policy;
